import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import DataTable from '../../components/atoms/DataTable';
import { GetLocationRoutes } from '../../libs/GraphQL';
import { ROUTE_COLUMNS } from './constants';
import { buildDateList, applyClientFilters } from './utils';

const toastOpts = {
  className: 'prettyToast',
  position: 'top-center',
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  theme: 'light',
};

async function fetchRoutesForRange(startDate, endDate) {
  const dates = buildDateList(startDate, endDate);
  const results = await Promise.allSettled(
    dates.map((stockingDate) => GetLocationRoutes({ stockingDate })),
  );
  const successful = [];
  const failedDates = [];
  results.forEach((result, idx) => {
    if (result.status === 'fulfilled') {
      const routes = result.value?.routes || [];
      successful.push(...routes);
    } else {
      failedDates.push(dates[idx]);
    }
  });
  return { routes: successful, failedDates, totalDates: dates.length };
}

function RouteReviewTab({ filters, fcByLocationId }) {
  const [routes, setRoutes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    let cancelled = false;
    setIsLoading(true);

    fetchRoutesForRange(filters.startDate, filters.endDate)
      .then(({ routes: fetched, failedDates, totalDates }) => {
        if (cancelled || requestIdRef.current !== requestId) return;
        if (failedDates.length > 0 && failedDates.length === totalDates) {
          toast.error(
            `Failed fetching routes for all dates in range`,
            toastOpts,
          );
        } else if (failedDates.length > 0) {
          toast.warn(
            `Some dates failed to load: ${failedDates.join(', ')}`,
            toastOpts,
          );
        }
        setRoutes(fetched);
      })
      .catch((e) => {
        if (cancelled || requestIdRef.current !== requestId) return;
        console.error('RouteReviewTab fetch failed:', e);
        toast.error('Failed fetching routes. Please try again.', toastOpts);
        setRoutes([]);
      })
      .finally(() => {
        if (cancelled || requestIdRef.current !== requestId) return;
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [filters.startDate, filters.endDate]);

  // Enrich each route with fulfillmentCenter from the location lookup
  // (the LocationRoutes query does not populate it directly), and
  // generate a stable composite row key since route.id (= location id)
  // can repeat across stocking dates and stops.
  const enrichedRoutes = useMemo(
    () =>
      routes.map((route) => ({
        ...route,
        fulfillmentCenter:
          fcByLocationId[route.id] || route.fulfillmentCenter || '',
        rowKey: `${route.stockingDate}#${route.id}#${route.stockingIndex}#${route.stop}`,
      })),
    [routes, fcByLocationId],
  );

  const filteredRoutes = useMemo(
    () =>
      applyClientFilters(enrichedRoutes, {
        region: filters.region,
        market: filters.market,
      }),
    [enrichedRoutes, filters.region, filters.market],
  );

  return (
    <DataTable
      columns={ROUTE_COLUMNS}
      rows={filteredRoutes}
      rowKey="rowKey"
      emptyMessage="No routes for the selected filters."
      isLoading={isLoading}
    />
  );
}

RouteReviewTab.propTypes = {
  filters: PropTypes.shape({
    startDate: PropTypes.instanceOf(Date).isRequired,
    endDate: PropTypes.instanceOf(Date).isRequired,
    region: PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }).isRequired,
    market: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string,
        label: PropTypes.string,
      }),
    ).isRequired,
  }).isRequired,
  fcByLocationId: PropTypes.objectOf(PropTypes.string),
};

RouteReviewTab.defaultProps = {
  fcByLocationId: {},
};

export default RouteReviewTab;
